import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { track } from "../analytics";
import { useSyncedState } from "../live/useSyncedState";

/** One step of a guided tour (Scope A5). */
export interface TourStep {
  /** Route to navigate to before showing this step. */
  path?: string;
  /** `data-tour` value of the element to spotlight. Omit → a centered card. */
  target?: string;
  title: string;
  body: string;
  /** Side-effect run on entering the step (e.g. click a control to drive the UI). */
  run?: () => void;
}

export interface TourDef {
  id: string;
  label: string;
  description: string;
  steps: TourStep[];
}

/** The 2 highest-value tours (Scope A5 — the rest can be added incrementally). */
export const TOURS: TourDef[] = [
  {
    id: "add-client",
    label: "Add a client",
    description: "Intake → state-aware consent → cosmetic sign.",
    steps: [
      { path: "/add-client", target: "intake-form", title: "Start with intake", body: "Basic fields, pre-filled with obviously-fake sample data. Change the date of birth or state and watch everything below adapt." },
      { path: "/add-client", target: "consent-split", title: "Consent figures itself out", body: "Psy180 derives the consent path from the client's age and state — guardian vs. self-consent — using the same logic the live app uses." },
      { path: "/add-client", target: "state-ref", title: "Every state's rules, built in", body: "It knows each state's minor self-consent age, so you always know what to expect before you send anything." },
      { path: "/add-client", target: "continue-consent", title: "Then review & sign", body: "Continue to see the real consent templates (watermarked samples) and a cosmetic sign step — nothing is submitted or stored in this demo." },
    ],
  },
  {
    id: "client-side",
    label: "See it from the client's side",
    description: "Preview the audio / haptics / camera permission flow.",
    steps: [
      { path: "/session", target: "perspective-toggle", title: "Two views, one toggle", body: "Your session has a therapist view and a client view. Flip between them anytime — no reload." },
      {
        path: "/session",
        target: "client-priming",
        title: "Exactly what your client sees",
        body: "The same ball, synced live — plus the real permission prompts they'll meet: tap-to-enable audio, controller pairing, and the camera prompt (shown here as a labelled illustration).",
        run: () => {
          const tab = Array.from(document.querySelectorAll('button[role="tab"]')).find((b) => /client view/i.test(b.textContent || ""));
          (tab as HTMLButtonElement | undefined)?.click();
        },
      },
    ],
  },
  {
    id: "schedule-appointments",
    label: "Schedule & manage appointments",
    description: "One-off + recurring sessions, and how reminders work.",
    steps: [
      { path: "/calendar", target: "dc-client", title: "Pick a client", body: "Appointments attach to a client on your roster. The demo is pre-loaded with obviously-fake sample clients." },
      { path: "/calendar", target: "dc-mode", title: "One-off or recurring?", body: "Choose a single session or a weekly repeating slot. Recurring slots get laid out months ahead automatically — no re-entering the same time every week." },
      { path: "/calendar", target: "dc-datetime", title: "Set the time", body: "One-off: pick a date and time. Recurring: pick a weekday and a time of day — the same slot each week." },
      { path: "/calendar", target: "dc-add", title: "Add it", body: "Add the appointment. Try one of each — a one-off and a recurring — and watch them land on the calendar below." },
      { path: "/calendar", target: "dc-grid", title: "See your month", body: "Appointments lay out by day. Click any day to prefill the form, or click an appointment to start the session or cancel it. Go ahead and cancel one — it's all in-memory here." },
      { path: "/calendar", target: "dc-reminders", title: "Reminders, handled", body: "Every appointment can remind you and your client at the lead times you choose. A minor's reminder routes to their guardian. This is where notifications come from — you find and adjust them under Settings → Account & Notifications." },
    ],
  },
];

interface TourCtx {
  active: TourDef | null;
  index: number;
  start: (id: string) => void;
  next: () => void;
  back: () => void;
  end: () => void;
}

const Ctx = createContext<TourCtx | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  // Co-browsed so a guided tour runs on BOTH screens in a co-driven demo — either
  // person can start it or press Back / Next (both screens run each step; see
  // TourOverlay). Store the tour id + index (both serializable); derive the TourDef
  // locally. useSyncedState === useState outside a live room.
  const [activeId, setActiveId] = useSyncedState<string | null>("tour.activeId", null);
  const [index, setIndex] = useSyncedState("tour.index", 0);
  const active = useMemo(() => TOURS.find((t) => t.id === activeId) ?? null, [activeId]);

  const start = useCallback(
    (id: string) => {
      const t = TOURS.find((x) => x.id === id) ?? null;
      if (t) track("tour_started", { tour: t.id });
      setActiveId(t?.id ?? null);
      setIndex(0);
    },
    [setActiveId, setIndex],
  );
  const end = useCallback(() => setActiveId(null), [setActiveId]);
  const next = useCallback(() => {
    if (!active) return;
    if (index + 1 >= active.steps.length) {
      track("tour_completed", { tour: active.id });
      setActiveId(null);
      setIndex(0);
    } else {
      setIndex(index + 1);
    }
  }, [active, index, setActiveId, setIndex]);
  const back = useCallback(() => setIndex(Math.max(0, index - 1)), [index, setIndex]);

  const value = useMemo(() => ({ active, index, start, next, back, end }), [active, index, start, next, back, end]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTour(): TourCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTour must be used within <TourProvider>");
  return c;
}
